import { DimensionalType } from "~/enums/dimensionalType";
import { NumberHelper } from "~/helpers/numberHelper";
import ModelMappingOriginFormElement from "~/models/mapping/modelMappingOriginFormElement";
import ModelMappingValueOrigin from "~/models/mapping/modelMappingValueOrigin";
import { OptionRangeType } from "../../enums/optionRangeType";
import FormElement from "../../../formability/form_builder/models/formElements/formElement";
import { Capability } from "../../../formability/form_builder/models/formElements/formElementCapability";
import FormElementCapabilityManager from "../../../formability/form_builder/models/formElements/formElementCapabilityManager";
import FormElementDefinition from "../../definitions/formElementDefinition";
import { FormElementType } from "../../enums/formElementType";
import { PaymentInformationTypeDisplay } from "../../../formability/mappingBuilder/enums/payment-information-type";
import {
  SubmissionInformationType,
  SubmissionInformationTypeDisplay
} from "../../../formability/mappingBuilder/enums/submission-information-type";

export default class ModelMappingValueOriginFormElement extends ModelMappingValueOrigin<FormElement, ModelMappingOriginFormElement> {
  get origin(): FormElement {
    return FormElement.repo.find(this.id)
  }

  get value() {
    const parent = this.modelMappingOrigin.origin
    if (parent.is_repeatable) {
      return {label: this.origin?.mappingLabelTitle}
    } else { //parent is form
      if (this.origin) {
        return {label: this.origin.mappingLabelTitle}
      } else {
        // value is of SubmissionInformation type
        const id = this.id ? this.id.split('__') : [""]
        const ws = id[1] ? this.modelMappingOrigin.origin['workflow_steps'][id[1]] : null;

        if (ws && ws.isPaymentStep && parseInt(id[0], 10) !== SubmissionInformationType.WorkflowStepCompletion && parseInt(id[0], 10) !== SubmissionInformationType.WorkflowStepStart) {
          return {label: `${PaymentInformationTypeDisplay[id[0]]?.label} for ${ws.titleOrDefault}`}
        }

        if (parseInt(id[0], 10) === SubmissionInformationType.WorkflowStepCompletion) {
          if (ws) {
            return {label: `Completion Date of ${ws.titleOrDefault}`};
          } else return null;
        } else if (parseInt(id[0], 10) === SubmissionInformationType.WorkflowStepStart) {
          if (ws) {
            return {label: `Start Date of ${ws.titleOrDefault}`};
          } else return null;
        } else {
          return {label: SubmissionInformationTypeDisplay[id[0]]?.label}
        }
      }
    }
  }

  get valueType(): string {
    if (this.modelMappingOrigin.origin.is_repeatable) {
      if (FormElementCapabilityManager.LAYOUT_CONTAINER_CONTROLS.includes(this.modelMappingOrigin.origin.element_type)) {
        return FormElementDefinition.getDisplayNameSafe(this.origin?.element_type)
      } else {
        return FormElementDefinition.getDisplayNameSafe(this.modelMappingOrigin.origin.element_type)
      }
    } else {
      const id = this.id ? this.id.split('__') : [""]
      const ws = id[1] ? this.modelMappingOrigin.origin['workflow_steps'][id[1]] : null;

      if (ws && ws.isPaymentStep && parseInt(id[0], 10) !== SubmissionInformationType.WorkflowStepCompletion && parseInt(id[0], 10) !== SubmissionInformationType.WorkflowStepStart) {
        return PaymentInformationTypeDisplay[id[0]].type;
      }

      const enumValue = id[0]
      if ((id.length === 1 && SubmissionInformationType[enumValue]) || (!enumValue.includes('-') && (SubmissionInformationType.WorkflowStepCompletion === parseInt(enumValue, 10) || SubmissionInformationType.WorkflowStepStart === parseInt(enumValue, 10)))) {
        return SubmissionInformationTypeDisplay[enumValue]?.type
      } else {
        if (this.origin?.is_readonly || this.modelMappingOrigin.origin?.is_readonly) {
          return 'read'
        } else {
          return FormElementDefinition.getDisplayNameSafe(this.origin?.element_type || this.modelMappingOrigin.origin?.element_type)
        }
      }
    }
  }

  get isBuiltin(): boolean {
    // 6__ special SubmissionInformationType builtin with extra metadata reference to form workflow step
    return this.id && (this.id.startsWith(`${SubmissionInformationType.WorkflowStepCompletion}__`) || this.id.startsWith(`${SubmissionInformationType.WorkflowStepStart}__`) || Object.keys(SubmissionInformationType).map(e => e.toString()).includes(this.id))
  }

  get referencesWorkflowStep(): boolean {
    return this.id && (this.id.startsWith(`${SubmissionInformationType.WorkflowStepCompletion}__`) || this.id.startsWith(`${SubmissionInformationType.WorkflowStepStart}__`));
  }

  get enumValueType(): FormElementType {
    if (this.origin) {
      return this.origin.element_type
    }
  }

  get isIndexed(): boolean {
    if (this.origin) {
      return this.modelMappingOrigin.origin.rootModel.indexed_element_ids.includes(this.origin.client_id)
    }
  }

  get isUploader(): boolean {
    return this.origin?.isTypeOneOf([FormElementType.uploadFiles, FormElementType.imageUpload]);
  }

  get isSinglePicklist(): boolean {
    const element = this.origin
    if (!element) return false
    if (element.can(Capability.optionRangeRequirable)) {
      if (!element.is_required) return false // means input can be between 0 and all choices
      switch (element.option_range_type) {
        case OptionRangeType.Exactly:
          return element.checkbox_range_min === 1
        case OptionRangeType.All: // intentional fallthrough
        case OptionRangeType.AtLeast:
          return false
        case OptionRangeType.AtMost:
          return NumberHelper.isInteger(element.checkbox_range_max) &&
          element.checkbox_range_max <= 1
        case OptionRangeType.Between: // intentional fallthrough
        default:
          return NumberHelper.isInteger(element.checkbox_range_min) &&
            element.checkbox_range_min === 1 &&
            NumberHelper.isInteger(element.checkbox_range_max) &&
            element.checkbox_range_max === 1
      }
    }
  }

  get isMultiPicklist(): boolean {
    const element = this.origin
    if (!element) return false
    if (element.can(Capability.optionRangeRequirable)) {
      if (!element.is_required) return true // means input can be between 0 and all choices
      switch (element.option_range_type) {
        case OptionRangeType.All: // intentional fallthrough
        case OptionRangeType.AtLeast:
          return true
        case OptionRangeType.Exactly: // intentional fallthrough
          return !NumberHelper.isInteger(element.checkbox_range_min) || element.checkbox_range_min > 1
        case OptionRangeType.AtMost: // intentional fallthrough
        case OptionRangeType.Between: // intentional fallthrough
        default:
          return !NumberHelper.isInteger(element.checkbox_range_max) ||
            element.checkbox_range_max > 1
      }
    }
  }

  get fileDimensionalType(): DimensionalType {
    if (!this.isUploader) { return null; }

    return this.origin.is_multiple ? DimensionalType.Multi : DimensionalType.Single;
  }
}